import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, Edit, Trash2, Loader2, Route, Truck, Package, 
  FileText, Copy, CheckCircle, MapPin, Weight, Users,
  Building, Clock, DollarSign, Save, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useBillingPolicy } from '../../hooks/useBillingPolicy';
import { cn } from '../../lib/utils';
import AddressInput from '../../components/AddressInput';
import VehicleSelector from '../../components/VehicleSelector';
import { vehicleTypes, operationTypes, merchandiseTypes, containerTypes } from '../../mockData';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMS } from '../../utils/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Types de templates
const TEMPLATE_TYPES = {
  operation: {
    label: 'Opération / Transfert',
    icon: Route,
    color: 'bg-blue-100 text-blue-700',
    description: 'Template pour créer rapidement des transferts récurrents'
  },
  contract: {
    label: 'Contrat',
    icon: FileText,
    color: 'bg-emerald-100 text-emerald-700',
    description: 'Template pour créer des contrats type'
  }
};

const TemplatesManagement = () => {
  const { hasPermission, isAdmin } = usePermissions();
  // RBAC V2 Phase 6 — settings.templates.manage (create/edit) + admin-only pour delete
  const canManage = isAdmin || hasPermission(PERMS.SETTINGS_TEMPLATES_MANAGE);
  const canDelete = isAdmin; // STRICT admin only côté backend
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('operation');
  const [showContainerOptions, setShowContainerOptions] = useState(false);

  // Form state
  const billingPolicy = useBillingPolicy();
  const vatBlocked = !!billingPolicy?.vatBlocked;
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'operation',
    // Opération fields
    departureAddress: '',
    departureCoordinates: null,
    arrivalAddress: '',
    arrivalCoordinates: null,
    vehicleType: '',
    operationType: '',
    merchandiseType: '',
    containerType: '',
    weight: 1,
    numberOfTrucks: 1,
    shift: 'day',
    plannedStartTime: '06:00',
    plannedEndTime: '18:00',
    unitPrice: 0,
    notes: '',
    // Contract fields
    terminology: 'transfert',
    billingType: 'forfait',
    billingFrequency: 'mensuel',
    autoRenewal: false,
    defaultDuration: 12,
    // Extended contract fields
    monthlyForfait: null,
    pricePerUnit: null,
    includedUnits: null,
    extraUnitPrice: null,
    applyTVA: true,
    tvaRate: 18,
    paymentTermDays: 30,
    paymentMethod: 'virement',
    penaltyRate: null,
    defaultVehicleType: '',
    maxVehicles: null,
    maxDrivers: null,
    contractNotes: '',
    specialConditions: ''
  });

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Si l'API n'existe pas encore, utiliser des données vides
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Reset form
  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      type: 'operation',
      departureAddress: '',
      departureCoordinates: null,
      arrivalAddress: '',
      arrivalCoordinates: null,
      vehicleType: '',
      operationType: '',
      merchandiseType: '',
      containerType: '',
      weight: 1,
      numberOfTrucks: 1,
      shift: 'day',
      plannedStartTime: '06:00',
      plannedEndTime: '18:00',
      unitPrice: 0,
      notes: '',
      terminology: 'transfert',
      billingType: 'forfait',
      billingFrequency: 'mensuel',
      autoRenewal: false,
      defaultDuration: 12,
      monthlyForfait: null,
      pricePerUnit: null,
      includedUnits: null,
      extraUnitPrice: null,
      applyTVA: true,
      tvaRate: 18,
      paymentTermDays: 30,
      paymentMethod: 'virement',
      penaltyRate: null,
      defaultVehicleType: '',
      maxVehicles: null,
      maxDrivers: null,
      contractNotes: '',
      specialConditions: ''
    });
    setEditingTemplate(null);
    setShowContainerOptions(false);
  };

  // Open modal for new template
  const handleNew = (type = 'operation') => {
    resetForm();
    setForm(prev => ({ ...prev, type }));
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name || '',
      description: template.description || '',
      type: template.type || 'operation',
      departureAddress: template.departureAddress || '',
      departureCoordinates: template.departureCoordinates || null,
      arrivalAddress: template.arrivalAddress || '',
      arrivalCoordinates: template.arrivalCoordinates || null,
      vehicleType: template.vehicleType || '',
      operationType: template.operationType || '',
      merchandiseType: template.merchandiseType || '',
      containerType: template.containerType || '',
      weight: template.weight || 1,
      numberOfTrucks: template.numberOfTrucks || 1,
      shift: template.shift || 'day',
      plannedStartTime: template.plannedStartTime || '06:00',
      plannedEndTime: template.plannedEndTime || '18:00',
      unitPrice: template.unitPrice || 0,
      notes: template.notes || '',
      terminology: template.terminology || 'transfert',
      billingType: template.billingType || 'forfait',
      billingFrequency: template.billingFrequency || 'mensuel',
      autoRenewal: template.autoRenewal || false,
      defaultDuration: template.defaultDuration || 12,
      monthlyForfait: template.monthlyForfait || null,
      pricePerUnit: template.pricePerUnit || null,
      includedUnits: template.includedUnits || null,
      extraUnitPrice: template.extraUnitPrice || null,
      applyTVA: template.applyTVA !== false,
      tvaRate: template.tvaRate || 18,
      paymentTermDays: template.paymentTermDays || 30,
      paymentMethod: template.paymentMethod || 'virement',
      penaltyRate: template.penaltyRate || null,
      defaultVehicleType: template.defaultVehicleType || '',
      maxVehicles: template.maxVehicles || null,
      maxDrivers: template.maxDrivers || null,
      contractNotes: template.contractNotes || '',
      specialConditions: template.specialConditions || ''
    });
    
    // Check if container options should be shown
    const selectedOp = operationTypes.find(op => op.value === template.operationType);
    setShowContainerOptions(selectedOp?.showContainerOption || false);
    
    setShowModal(true);
  };

  // Save template
  const handleSave = async () => {
    if (!form.name) {
      toast.error('Le nom du template est requis');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        updatedAt: new Date().toISOString()
      };

      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, payload);
        toast.success('Template mis à jour');
      } else {
        payload.createdAt = new Date().toISOString();
        await axios.post(`${API}/templates`, payload);
        toast.success('Template créé');
      }

      setShowModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (template) => {
    if (!window.confirm(`Supprimer le template "${template.name}" ?`)) return;

    try {
      await axios.delete(`${API}/templates/${template.id}`);
      toast.success('Template supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Duplicate template
  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setForm({
      ...template,
      name: `${template.name} (copie)`,
      id: undefined
    });
    setShowModal(true);
  };

  // Handle operation type change
  const handleOperationTypeChange = (value) => {
    setForm(prev => ({ ...prev, operationType: value }));
    const selectedOp = operationTypes.find(op => op.value === value);
    setShowContainerOptions(selectedOp?.showContainerOption || false);
  };

  // Filter templates by type
  const operationTemplates = templates.filter(t => t.type === 'operation');
  const contractTemplates = templates.filter(t => t.type === 'contract');

  // Get labels
  const getVehicleTypeLabel = (value) => vehicleTypes.find(v => v.id === value)?.name || value;
  const getOperationTypeLabel = (value) => operationTypes.find(o => o.value === value)?.label?.replace(/^[^\s]+\s/, '') || value;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gestion des Templates</h2>
          <p className="text-sm text-gray-500">Créez des modèles réutilisables pour accélérer la création d'opérations et de contrats</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="operation" className="flex items-center gap-2">
            <Route className="w-4 h-4" />
            Opérations ({operationTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="contract" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Contrats ({contractTemplates.length})
          </TabsTrigger>
        </TabsList>

        {/* Operation Templates */}
        <TabsContent value="operation" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Templates pour créer rapidement des transferts avec trajets pré-configurés
            </p>
            <Button onClick={() => handleNew('operation')} className="bg-red-500 hover:bg-red-600" disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : ''} data-testid="new-operation-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Template
            </Button>
          </div>

          {operationTemplates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Route className="w-12 h-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun template d'opération</h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  Créez des templates pour les trajets récurrents comme Abidjan-Ouagadougou
                </p>
                <Button onClick={() => handleNew('operation')} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {operationTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        {template.description && (
                          <CardDescription className="text-xs mt-1">{template.description}</CardDescription>
                        )}
                      </div>
                      <Badge className="bg-blue-100 text-blue-700">
                        <Route className="w-3 h-3 mr-1" />
                        Opération
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Trajet */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">D</div>
                        <span className="text-gray-600 truncate">{template.departureAddress || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">A</div>
                        <span className="text-gray-600 truncate">{template.arrivalAddress || '-'}</span>
                      </div>
                    </div>

                    {/* Détails */}
                    <div className="flex flex-wrap gap-2">
                      {template.vehicleType && (
                        <Badge variant="outline" className="text-xs">
                          <Truck className="w-3 h-3 mr-1" />
                          {getVehicleTypeLabel(template.vehicleType)}
                        </Badge>
                      )}
                      {template.numberOfTrucks > 1 && (
                        <Badge variant="outline" className="text-xs">
                          {template.numberOfTrucks} camions
                        </Badge>
                      )}
                      {template.unitPrice > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <DollarSign className="w-3 h-3 mr-1" />
                          {new Intl.NumberFormat('fr-FR').format(template.unitPrice)} FCFA
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(template)} disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : 'Dupliquer'} data-testid={`duplicate-template-${template.id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(template)} disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : 'Modifier'} data-testid={`edit-template-${template.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(template)} disabled={!canDelete} title={!canDelete ? 'Suppression réservée aux admins' : 'Supprimer'} data-testid={`delete-template-${template.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contract Templates */}
        <TabsContent value="contract" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Templates pour créer des contrats avec des conditions pré-définies
            </p>
            <Button onClick={() => handleNew('contract')} className="bg-red-500 hover:bg-red-600" disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : ''} data-testid="new-contract-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Template
            </Button>
          </div>

          {contractTemplates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun template de contrat</h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  Créez des templates pour les types de contrats récurrents
                </p>
                <Button onClick={() => handleNew('contract')} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contractTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        {template.description && (
                          <CardDescription className="text-xs mt-1">{template.description}</CardDescription>
                        )}
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <FileText className="w-3 h-3 mr-1" />
                        Contrat
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Info principales */}
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs capitalize">
                        {template.terminology}s
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {template.billingType === 'forfait' ? 'Forfait' : template.billingType}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {template.defaultDuration} mois
                      </Badge>
                      {template.autoRenewal && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                          Renouvellement auto
                        </Badge>
                      )}
                    </div>
                    
                    {/* Tarification */}
                    <div className="text-sm text-gray-600 space-y-1">
                      {template.billingType === 'forfait' && template.monthlyForfait && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          <span>{new Intl.NumberFormat('fr-FR').format(template.monthlyForfait)} FCFA/mois</span>
                          {template.includedUnits && (
                            <span className="text-gray-400">({template.includedUnits} inclus)</span>
                          )}
                        </div>
                      )}
                      {template.pricePerUnit && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          <span>{new Intl.NumberFormat('fr-FR').format(template.pricePerUnit)} FCFA/unité</span>
                        </div>
                      )}
                      {template.applyTVA && (
                        <span className="text-xs text-gray-400">TVA {template.tvaRate || 18}%</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(template)} disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : 'Dupliquer'} data-testid={`duplicate-template-${template.id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(template)} disabled={!canManage} title={!canManage ? 'Permission requise : settings.templates.manage' : 'Modifier'} data-testid={`edit-template-${template.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(template)} disabled={!canDelete} title={!canDelete ? 'Suppression réservée aux admins' : 'Supprimer'} data-testid={`delete-template-${template.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Modifier le template' : `Nouveau template ${form.type === 'operation' ? 'd\'opération' : 'de contrat'}`}
            </DialogTitle>
            <DialogDescription>
              {form.type === 'operation' 
                ? 'Configurez un template pour créer rapidement des transferts'
                : 'Configurez un template pour créer des contrats type'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom du template *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Abidjan-Ouagadougou, Location mensuelle standard..."
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description optionnelle..."
                  rows={2}
                />
              </div>
            </div>

            {/* Operation Template Fields */}
            {form.type === 'operation' && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Route className="w-4 h-4" />
                  Configuration du trajet
                </h3>

                {/* Addresses */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">D</div>
                      Adresse de départ
                    </Label>
                    <AddressInput
                      value={form.departureAddress}
                      onChange={(value, coords) => setForm(prev => ({ 
                        ...prev, 
                        departureAddress: value,
                        departureCoordinates: coords
                      }))}
                      placeholder="Ex: Port Autonome d'Abidjan"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">A</div>
                      Adresse d'arrivée
                    </Label>
                    <AddressInput
                      value={form.arrivalAddress}
                      onChange={(value, coords) => setForm(prev => ({ 
                        ...prev, 
                        arrivalAddress: value,
                        arrivalCoordinates: coords
                      }))}
                      placeholder="Ex: Ouagadougou, Burkina Faso"
                    />
                  </div>
                </div>

                {/* Vehicle Type */}
                <div>
                  <Label>Type de véhicule</Label>
                  <VehicleSelector
                    vehicles={vehicleTypes}
                    selected={form.vehicleType}
                    onSelect={(value) => setForm(prev => ({ ...prev, vehicleType: value }))}
                  />
                </div>

                {/* Operation & Merchandise */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nature de l'opération</Label>
                    <Select value={form.operationType || undefined} onValueChange={handleOperationTypeChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {operationTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {showContainerOptions ? (
                    <div>
                      <Label>Type de conteneur</Label>
                      <Select value={form.containerType || undefined} onValueChange={(v) => setForm(prev => ({ ...prev, containerType: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {containerTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label>Type de marchandise</Label>
                      <Select value={form.merchandiseType || undefined} onValueChange={(v) => setForm(prev => ({ ...prev, merchandiseType: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {merchandiseTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Numbers */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Weight className="w-3 h-3" />
                      Poids (tonnes)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.weight}
                      onChange={(e) => setForm(prev => ({ ...prev, weight: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Nombre de camions
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.numberOfTrucks}
                      onChange={(e) => setForm(prev => ({ ...prev, numberOfTrucks: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Tarif unitaire (FCFA)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.unitPrice}
                      onChange={(e) => setForm(prev => ({ ...prev, unitPrice: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>

                {/* Shift */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Shift par défaut</Label>
                    <Select value={form.shift} onValueChange={(v) => setForm(prev => ({ ...prev, shift: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Jour</SelectItem>
                        <SelectItem value="night">Nuit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Heure de début</Label>
                    <Input
                      type="time"
                      value={form.plannedStartTime}
                      onChange={(e) => setForm(prev => ({ ...prev, plannedStartTime: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Heure de fin</Label>
                    <Input
                      type="time"
                      value={form.plannedEndTime}
                      onChange={(e) => setForm(prev => ({ ...prev, plannedEndTime: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes par défaut</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Instructions par défaut..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Contract Template Fields */}
            {form.type === 'contract' && (
              <div className="space-y-6">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Configuration du contrat
                </h3>

                {/* Terminologie et durée */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Terminologie</Label>
                    <Select value={form.terminology} onValueChange={(v) => setForm(prev => ({ ...prev, terminology: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transfert">Transferts</SelectItem>
                        <SelectItem value="operation">Opérations</SelectItem>
                        <SelectItem value="course">Courses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Durée par défaut (mois)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.defaultDuration}
                      onChange={(e) => setForm(prev => ({ ...prev, defaultDuration: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="autoRenewal"
                        checked={form.autoRenewal}
                        onChange={(e) => setForm(prev => ({ ...prev, autoRenewal: e.target.checked }))}
                        className="w-4 h-4 text-red-600 rounded"
                      />
                      <Label htmlFor="autoRenewal" className="cursor-pointer text-sm">
                        Renouvellement auto
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Facturation */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Facturation
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type de facturation</Label>
                      <Select value={form.billingType} onValueChange={(v) => setForm(prev => ({ ...prev, billingType: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="forfait">Forfait mensuel</SelectItem>
                          <SelectItem value="unite">À l'unité</SelectItem>
                          <SelectItem value="kilometre">Au kilomètre</SelectItem>
                          <SelectItem value="tonne">À la tonne</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fréquence de facturation</Label>
                      <Select value={form.billingFrequency} onValueChange={(v) => setForm(prev => ({ ...prev, billingFrequency: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ponctuel">Ponctuel</SelectItem>
                          <SelectItem value="hebdomadaire">Hebdomadaire</SelectItem>
                          <SelectItem value="mensuel">Mensuel</SelectItem>
                          <SelectItem value="trimestriel">Trimestriel</SelectItem>
                          <SelectItem value="annuel">Annuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.billingType === 'forfait' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Forfait mensuel (FCFA)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.monthlyForfait || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, monthlyForfait: parseInt(e.target.value) || null }))}
                          placeholder="Ex: 5000000"
                        />
                      </div>
                      <div>
                        <Label>Unités incluses</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.includedUnits || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, includedUnits: parseInt(e.target.value) || null }))}
                          placeholder="Ex: 20"
                        />
                      </div>
                      <div>
                        <Label>Tarif unité supplémentaire</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.extraUnitPrice || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, extraUnitPrice: parseInt(e.target.value) || null }))}
                          placeholder="Ex: 150000"
                        />
                      </div>
                    </div>
                  )}

                  {form.billingType !== 'forfait' && (
                    <div>
                      <Label>Tarif par unité (FCFA)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.pricePerUnit || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, pricePerUnit: parseInt(e.target.value) || null }))}
                        placeholder="Ex: 200000"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="applyTVA"
                        checked={form.applyTVA && !vatBlocked}
                        disabled={vatBlocked}
                        onChange={(e) => setForm(prev => ({ ...prev, applyTVA: e.target.checked }))}
                        className={`w-4 h-4 rounded ${vatBlocked ? 'cursor-not-allowed opacity-60' : 'text-red-600'}`}
                      />
                      <Label htmlFor="applyTVA" className={`text-sm ${vatBlocked ? 'text-gray-500 cursor-not-allowed' : 'cursor-pointer'}`}>
                        Appliquer la TVA
                        {vatBlocked && <span className="ml-2 text-xs uppercase tracking-wide text-gray-500">indisponible</span>}
                      </Label>
                    </div>
                    {form.applyTVA && !vatBlocked && (
                      <div>
                        <Label>Taux TVA (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={form.tvaRate}
                          onChange={(e) => setForm(prev => ({ ...prev, tvaRate: parseFloat(e.target.value) || 18 }))}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Conditions de paiement */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Conditions de paiement
                  </h4>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Délai de paiement (jours)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.paymentTermDays}
                        onChange={(e) => setForm(prev => ({ ...prev, paymentTermDays: parseInt(e.target.value) || 30 }))}
                      />
                    </div>
                    <div>
                      <Label>Mode de paiement</Label>
                      <Select value={form.paymentMethod} onValueChange={(v) => setForm(prev => ({ ...prev, paymentMethod: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="virement">Virement bancaire</SelectItem>
                          <SelectItem value="cheque">Chèque</SelectItem>
                          <SelectItem value="especes">Espèces</SelectItem>
                          <SelectItem value="mobile">Mobile Money</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pénalité retard (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.penaltyRate || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, penaltyRate: parseFloat(e.target.value) || null }))}
                        placeholder="Ex: 1.5"
                      />
                    </div>
                  </div>
                </div>

                {/* Ressources */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Ressources par défaut
                  </h4>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Type de véhicule par défaut</Label>
                      <Select 
                        value={form.defaultVehicleType || undefined} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, defaultVehicleType: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Max. véhicules dédiés</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.maxVehicles || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, maxVehicles: parseInt(e.target.value) || null }))}
                        placeholder="Illimité"
                      />
                    </div>
                    <div>
                      <Label>Max. chauffeurs dédiés</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.maxDrivers || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, maxDrivers: parseInt(e.target.value) || null }))}
                        placeholder="Illimité"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                  <div>
                    <Label>Notes générales du contrat</Label>
                    <Textarea
                      value={form.contractNotes}
                      onChange={(e) => setForm(prev => ({ ...prev, contractNotes: e.target.value }))}
                      placeholder="Notes qui apparaîtront dans le contrat..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Conditions spéciales</Label>
                    <Textarea
                      value={form.specialConditions}
                      onChange={(e) => setForm(prev => ({ ...prev, specialConditions: e.target.value }))}
                      placeholder="Conditions particulières applicables..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-500 hover:bg-red-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editingTemplate ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesManagement;
